// Small shared UI primitives and an async-action hook.

import React from "react";
import { ApiClientError } from "../api/client";

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="spinner-dot" /> {label ?? "Working…"}
    </span>
  );
}

export function ErrorBanner({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  if (!error) return null;
  const msg =
    error instanceof ApiClientError
      ? `${error.message}${error.reason ? ` (${error.reason})` : ""}`
      : error instanceof Error
        ? error.message
        : String(error);
  const retryable = error instanceof ApiClientError ? error.retryable : true;
  return (
    <div className="error-banner" role="alert">
      <span>⚠ {msg}</span>
      {onRetry && retryable && (
        <button className="btn btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function Button({
  children,
  loading,
  variant = "primary",
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  variant?: "primary" | "ghost" | "danger";
}) {
  return (
    <button className={`btn btn-${variant}`} disabled={loading || rest.disabled} {...rest}>
      {loading ? <Spinner /> : children}
    </button>
  );
}

/** Track an async action's pending/error state with a runner. */
export function useAsync<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>
) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  const run = React.useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      setLoading(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [fn]
  );

  return { loading, error, run, clearError: () => setError(null) };
}
